/* Optional project additions. No cart change occurs until a customer submits a form. */
(() => {
  if (customElements.get('cp-upsell-pilot')) return;
  class ProjectPilot extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.addEventListener('submit', event => this.submit(event));
      this.addEventListener('change', () => this.updateTotal());
      this.updateTotal();
      this.refreshCart();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCart());
      }
      this.publishEvent('cp_project_pilot_viewed', { product_id: this.dataset.product });
    }
    disconnectedCallback() { this.unsubscribe?.(); }
    publishEvent(name, data) {
      // Shopify's existing customer-event framework manages privacy/consent. No new pixel or endpoint.
      try { window.Shopify?.analytics?.publish(name, { pilot: this.dataset.pilot, ...data }); } catch (_) {}
    }
    updateTotal() {
      const selected = [...this.querySelectorAll('[data-pilot-colour]:checked')].filter(input => !input.disabled);
      const total = selected.reduce((sum, input) => sum + Number(input.dataset.price), 0);
      const output = this.querySelector('[data-pilot-total]');
      if (output) output.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.dataset.currency
      }).format(total / 100);
      const button = this.querySelector('[data-pilot-add-colours]');
      if (button && !this.busy) button.disabled = selected.length === 0;
    }
    async refreshCart() {
      try {
        const response = await fetch(this.dataset.cartUrl, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) return;
        const cart = await response.json();
        const products = new Set(cart.items.map(item => String(item.product_id)));
        const variants = new Set(cart.items.map(item => String(item.variant_id)));
        this.querySelectorAll('[data-pilot-card]').forEach(card => {
          card.hidden = products.has(card.dataset.pilotCard);
        });
        this.querySelectorAll('[data-pilot-colour]').forEach(input => {
          const inCart = variants.has(input.value);
          input.closest('label').hidden = inCart;
          input.disabled = inCart;
          if (inCart) input.checked = false;
        });
        const palette = this.querySelector('[data-pilot-palette]');
        if (palette) palette.hidden = ![...this.querySelectorAll('[data-pilot-colour]')].some(input => !input.disabled);
        this.updateTotal();
      } catch (_) { /* The normal product form remains available if the cart cannot be read. */ }
    }
    async submit(event) {
      const form = event.target.closest('[data-pilot-form]');
      if (!form) return;
      event.preventDefault();
      if (this.busy) return;
      const isPalette = form.dataset.pilotForm === 'palette';
      const ids = isPalette
        ? [...form.querySelectorAll('[data-pilot-colour]:checked')].filter(input => !input.disabled).map(input => input.value)
        : [new FormData(form).get('id')].filter(Boolean);
      if (!ids.length) return;
      const status = this.querySelector('[data-pilot-status]');
      const button = form.querySelector('[type="submit"]');
      this.busy = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      status.textContent = '';
      let added = false;
      try {
        const cart = document.querySelector('cart-drawer');
        const sections = cart?.getSectionsToRender?.().map(section => section.id) || [];
        const payload = {
          items: ids.map(id => ({ id: Number(id), quantity: 1, properties: {
            _cp_upsell_pilot: this.dataset.pilot,
            _cp_upsell_origin: this.dataset.product,
            _cp_upsell_type: isPalette ? 'palette' : 'project_addition'
          } }))
        };
        if (sections.length) {
          payload.sections = [...new Set(sections)];
          payload.sections_url = window.location.pathname;
          cart.setActiveElement?.(button);
        }
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload)
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(this.dataset.error);
        added = true;
        status.textContent = this.dataset.added;
        if (isPalette) form.querySelectorAll('[data-pilot-colour]').forEach(input => { input.checked = false; });
        this.publishEvent('cp_project_pilot_added', { product_id: this.dataset.product, variant_ids: ids, quantity: ids.length });
        if (cart && result.sections) {
          try { cart.classList.remove('is-empty'); cart.renderContents(result); }
          catch (_) { window.location.assign(this.dataset.cartPage); }
        } else {
          window.location.assign(this.dataset.cartPage);
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cp-project-pilot', cartData: result });
        }
        await this.refreshCart();
      } catch (_) {
        // Never retry a POST automatically: an interrupted response can already have added the items.
        status.textContent = added ? this.dataset.added : this.dataset.error;
      } finally {
        this.busy = false;
        button.removeAttribute('aria-busy');
        button.disabled = false;
        this.updateTotal();
      }
    }
  }
  customElements.define('cp-upsell-pilot', ProjectPilot);
})();
